import { ColumnType } from '@shared/enums/ColumnType';
import { ContactType } from '@shared/enums/ContactType';
import { InvitationStatusType } from '@shared/enums/InvitationStatusType';
import { MailjetStatusType } from '@shared/enums/MailjetStatusType';
import { TicketHolderType } from '@shared/enums/TicketHolderType';
import { SortedPage } from '@shared/models/sortedPage';
import { IContact } from '@shared/services/api/legacy/contacts-service/contacts-model';
import { IRegistration } from '@shared/services/api/legacy/landingpages-service/registrationpage-model';

export interface IInvitationsToEdit {
    invitations: IInvitation[];
    idsToDecline: string[];
    idsToDelete: string[];
}

export class InvitationsToEdit implements IInvitationsToEdit {
    constructor(
        public invitations: IInvitation[],
        public idsToDecline: string[],
        public idsToDelete: string[]
    ) { }
}

export interface IInvitation {
    id: string;
    userId: string;
    contactId: string;
    contactFirstName: string;
    contactLastNamePrefix: string;
    contactLastName: string;
    contactCompanyName: string;
    ticketHolderName: string;
    maxGuests: number;
    index: number;
    statusType: InvitationStatusType;
    mailjetStatus: MailjetStatusType;
    newAttendees: IRegistration;
    isGdprProcessed: boolean;
}

export class Invitation implements IInvitation {
    constructor(
        public id: string,
        public userId: string,
        public contactId: string,
        public contactFirstName: string,
        public contactLastNamePrefix: string,
        public contactLastName: string,
        public contactCompanyName: string,
        public ticketHolderName: string,
        public maxGuests: number,
        public index: number,
        public statusType: InvitationStatusType,
        public mailjetStatus: MailjetStatusType,
        public newAttendees: IRegistration,
        public isGdprProcessed: boolean
    ) { }
}

export interface IInvitationTotal {
    status: any;
}

export interface IAddInvitation {
    ticketHolderId: string;
    ticketHolderType: TicketHolderType;
    contacts: IContact[];
    groupIds: string[];
}

export class AddInvitation implements IAddInvitation {
    constructor(
        public ticketHolderId: string,
        public ticketHolderType: TicketHolderType,
        public contacts: IContact[],
        public groupIds: string[]
    ) { }
}

export interface IPrepareImportRequest {
    accountOwnerId: string;
    languageId: string;
    ticketHolderId: string;
    contactType: string;
    data: any[][];
    contactColumns: IContactColumn[];
}

export class PrepareImportRequest implements IPrepareImportRequest {
    constructor(
        public accountOwnerId: string,
        public languageId: string,
        public ticketHolderId: string,
        public contactType: string,
        public data: any[][],
        public contactColumns: IContactColumn[]
    ) { }
}

export interface IContactColumn {
    columnIndex: number;
    columnId: string;
    columnType: ColumnType;
}

export class ContactColumn implements IContactColumn {
    constructor(
        public columnIndex: number,
        public columnId: string,
        public columnType: ColumnType
    ) { }
}

export interface IAddNewContact {
    ticketHolderId: string;
    firstName: string;
    lastName: string;
    title: string;
    type: ContactType;
    genderId: string;
    languageId: string;
    email: string;
    phone: string;
    mobile: string;
    companyName: string;
    accountOwnerId: string;
    salutation: string;
    mailPermission: boolean;
    smsPermission: boolean;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    postalCode: string;
    country: string;
    crmId: string;
    customerNumber: string;
    groupIds: string[];
}

export class AddNewContact implements IAddNewContact {
    constructor(
        public ticketHolderId: string,
        public firstName: string,
        public lastName: string,
        public title: string,
        public type: ContactType,
        public genderId: string,
        public languageId: string,
        public email: string,
        public phone: string,
        public mobile: string,
        public companyName: string,
        public salutation: string,
        public mailPermission: boolean,
        public smsPermission: boolean,
        public accountOwnerId: string,
        public addressLineOne: string,
        public addressLineTwo: string,
        public city: string,
        public postalCode: string,
        public country: string,
        public crmId: string,
        public customerNumber: string,
        public groupIds: string[]
    ) { }
}

export class InvitationsFilterModel {
    // The invitation status filter dropdown
    public statusFilter = 0;
    // The invitation mail status filter dropdown
    public mailStatusFilter = 0;
    // The invitation ticket holder filter dropdown
    public ticketHolderFilter: string = null;
    // The invitation mail contact type filter dropdown
    public contactTypeFilter = 0;
    // The invitation search value
    public searchValue = '';
    // The invitation ticket holder filter dropdown
    public accountOwnerFilter: string = null;
    // The invitation group name filter dropdown
    public groupFilter: string = null;
}

export class InvitationsPage extends SortedPage {
    public filterModel: InvitationsFilterModel = new InvitationsFilterModel();
}

export class InvitationsPagedData<T> {
    public data = new Array<T>();
    public page = new InvitationsPage();
}

export interface IUpdateInvitation {
    id: string;
    ticketHolderId: string;
    ticketHolderType: TicketHolderType;
    maxGuests: number;
    inviteeGroupIds: string[];
    guestGroupIds: string[];
    languageId: string;
}

export class UpdateInvitation implements IUpdateInvitation {
    constructor(
        public id: string,
        public ticketHolderId: string,
        public ticketHolderType: TicketHolderType,
        public maxGuests: number,
        public inviteeGroupIds: string[],
        public guestGroupIds: string[],
        public languageId: string
    ) { }
}

export interface IUpdateBatchInvitation {
    ids: string[];
    isSelectAll: boolean;
    filterModel: InvitationsFilterModel;
    ticketHolderId: string;
    ticketHolderType: TicketHolderType;
    maxGuests: number;
    overwriteTicketHolder: boolean;
    groupsAddInvitee: string[];
    groupsAddGuest: string[];
    groupsRemoveInvitee: string[];
    groupsRemoveGuest: string[];
    languageId: string;
}

export class UpdateBatchInvitation implements IUpdateBatchInvitation {
    constructor(
        public ids: string[],
        public isSelectAll: boolean,
        public filterModel: InvitationsFilterModel,
        public ticketHolderId: string,
        public ticketHolderType: TicketHolderType,
        public maxGuests: number,
        public overwriteTicketHolder: boolean,
        public groupsAddInvitee: string[],
        public groupsAddGuest: string[],
        public groupsRemoveInvitee: string[],
        public groupsRemoveGuest: string[],
        public languageId: string
    ) { }
}
